/**
 * Merging persisted settings back over the code's defaults.
 *
 * `zustand/persist` merges shallowly: `{ ...defaults, ...persisted }`. For a
 * flat store that is right. This store is not flat — `cameraSettings`,
 * `secondaryCameraSettings`, `cardTerminal` and the quality blocks are nested
 * objects — and a shallow merge replaces each of those objects *wholesale* with
 * whatever was written to disk.
 *
 * So the moment a release adds a key inside one of them, every booth that saved
 * its settings before that release rehydrates with the new key `undefined`
 * instead of its default. The setting does not fall back; it ceases to exist,
 * and whatever reads it sees `undefined` — usually falsy, i.e. "off". That is
 * what "all my settings changed after the update" looks like from the booth.
 *
 * The store has been paying for this one key at a time. `terminalId`,
 * `captureFormat`, `focusMode`, `waitForFocusBeforeCapture` and
 * `previewQuality.maxFps` are each marked optional with a comment saying a
 * booth persisted before the key existed has no value stored, and each got a
 * hand-written fallback (`normaliseFocusMode`, `resolveCaptureFormat`,
 * `waitForFocusEnabled`, `?? false`) to cover for it. Those keys are therefore
 * already safe — and this merge leaves them exactly where they are.
 *
 * The cost shows up where somebody did not write that fallback.
 * `applyFilterOnPreview` defaults to `true` and CameraSettingsModal reads it
 * raw (`checked={settings.applyFilterOnPreview}`), so a booth that saved
 * `cameraSettings` before it shipped shows the operator a toggle sitting OFF
 * that they never touched.
 *
 * Deep-merging defaults *underneath* the persisted values fixes that case and
 * removes the need for the next per-key workaround, without taking anything
 * away from the operator: a key the operator has set keeps their value, a key
 * they have never seen gets the default.
 */

/** A plain `{}` object — not an array, a Date, a class instance or null. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const proto: unknown = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * `persisted` wins, `defaults` fills the gaps, at every depth.
 *
 * Only plain objects are recursed into. Arrays are replaced, not merged —
 * a persisted list of two printers must not inherit a third from the defaults.
 * A key the operator has explicitly set to `null` or `false` keeps that value;
 * only a genuinely absent key takes the default.
 */
export function mergeNestedDefaults<T>(defaults: T, persisted: unknown): T {
  if (!isPlainObject(persisted)) return defaults
  if (!isPlainObject(defaults)) return persisted as T

  const out: Record<string, unknown> = { ...defaults }
  for (const [key, persistedValue] of Object.entries(persisted)) {
    const defaultValue = (defaults as Record<string, unknown>)[key]
    out[key] =
      isPlainObject(defaultValue) && isPlainObject(persistedValue)
        ? mergeNestedDefaults(defaultValue, persistedValue)
        : persistedValue
  }
  return out as T
}
